import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import StructuredData from '@/components/base/StructuredData';

const privacySchemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Privacy Policy – 4K Dream',
    url: 'https://www.4k-dream.com/privacy',
    description: 'Privacy Policy for 4K Dream independent streaming service. Learn how we collect, use, and protect your personal data.',
    dateModified: '2026-04-03',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.4k-dream.com/' },
        { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: 'https://www.4k-dream.com/privacy' },
      ],
    },
    publisher: {
      '@type': 'Organization',
      name: '4K Dream',
      url: 'https://www.4k-dream.com',
    },
  },
];

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <StructuredData schemas={privacySchemas} id="privacy" />
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              alt="4K Dream Logo"
              className="h-9 w-auto object-contain"
              src="https://static.readdy.ai/image/5c7545dbb6198b28906c423b0244c4c9/0789dc6d730dd15b6f1e8e24be7c3580.webp"
            />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1a0a2e] via-[#0d0d1a] to-[#0a0a0f] py-16 sm:py-20 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-[#862ce0]/20 border border-[#862ce0]/30 rounded-full px-4 py-2 mb-6">
            <i className="ri-shield-check-line text-[#b47ef3] text-sm"></i>
            <span className="text-[#b47ef3] text-sm font-medium">Legal Document</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-white/60 text-lg">Last updated: April 3, 2026</p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="prose prose-invert max-w-none space-y-10">

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">1</span>
              Introduction
            </h2>
            <p className="text-white/70 leading-relaxed">
              Welcome to <strong className="text-white">4K Dream</strong> ("we", "our", or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our IPTV streaming services.
            </p>
            <p className="text-white/70 leading-relaxed mt-3">
              Please read this policy carefully. If you disagree with its terms, please discontinue use of our site and services.
            </p>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">2</span>
              Information We Collect
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">We may collect the following types of information:</p>
            <div className="space-y-4">
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <i className="ri-user-line text-[#b47ef3]"></i>
                  Personal Identification Information
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">Name, email address, phone number, billing address, and payment information provided when you register or purchase a subscription.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <i className="ri-device-line text-[#b47ef3]"></i>
                  Device & Technical Data
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">IP address, browser type, operating system, device identifiers, and usage data collected automatically when you access our services.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <i className="ri-bar-chart-line text-[#b47ef3]"></i>
                  Usage Data
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">Information about how you interact with our service, including channels watched, viewing duration, and feature usage patterns.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <i className="ri-spy-line text-[#b47ef3]"></i>
                  Cookies & Tracking Technologies
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">We use cookies and similar tracking technologies to enhance your experience, analyze traffic, and personalize content. You can control cookie settings through your browser.</p>
              </div>
            </div>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">3</span>
              How We Use Your Information
            </h2>
            <ul className="space-y-3 text-white/70">
              {[
                'To provide, operate, and maintain our streaming services',
                'To process transactions and send related information including purchase confirmations and invoices',
                'To send administrative information such as changes to our terms, conditions, and policies',
                'To respond to customer service requests and support needs',
                'To send promotional communications (you may opt out at any time)',
                'To monitor and analyze usage and trends to improve your experience',
                'To detect, prevent, and address technical issues and fraudulent activity',
                'To comply with legal obligations',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <i className="ri-check-line text-[#b47ef3] mt-0.5 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">4</span>
              Sharing Your Information
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
            </p>
            <ul className="space-y-3 text-white/70">
              {[
                'With service providers who assist us in operating our website and conducting our business (e.g., payment processors, hosting providers), subject to confidentiality agreements',
                'When required by law, regulation, or legal process',
                'To protect the rights, property, or safety of 4K Dream, our customers, or others',
                'In connection with a merger, acquisition, or sale of all or a portion of our assets',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-[#b47ef3] mt-0.5 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">5</span>
              Data Security
            </h2>
            <p className="text-white/70 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include SSL encryption, secure servers, and regular security audits.
            </p>
            <p className="text-white/70 leading-relaxed mt-3">
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
            </p>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">6</span>
              Data Retention
            </h2>
            <p className="text-white/70 leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When your data is no longer needed, we will securely delete or anonymize it.
            </p>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">7</span>
              Your Rights
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">Depending on your location, you may have the following rights regarding your personal data:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: 'ri-eye-line', title: 'Right to Access', desc: 'Request a copy of the personal data we hold about you' },
                { icon: 'ri-edit-line', title: 'Right to Rectification', desc: 'Request correction of inaccurate or incomplete data' },
                { icon: 'ri-delete-bin-line', title: 'Right to Erasure', desc: 'Request deletion of your personal data ("right to be forgotten")' },
                { icon: 'ri-pause-circle-line', title: 'Right to Restriction', desc: 'Request restriction of processing of your personal data' },
                { icon: 'ri-download-line', title: 'Right to Portability', desc: 'Receive your data in a structured, machine-readable format' },
                { icon: 'ri-close-circle-line', title: 'Right to Object', desc: 'Object to processing of your personal data for certain purposes' },
              ].map((r) => (
                <div key={r.title} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#862ce0]/20 flex-shrink-0">
                    <i className={`${r.icon} text-[#b47ef3] text-sm`}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">{r.title}</p>
                    <p className="text-white/50 text-xs mt-1">{r.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-white/70 leading-relaxed mt-4">
              To exercise any of these rights, please contact us at <a href="mailto:support@4k-dream.com" className="text-[#b47ef3] hover:underline">support@4k-dream.com</a>.
            </p>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">8</span>
              Children's Privacy
            </h2>
            <p className="text-white/70 leading-relaxed">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us and we will take steps to delete such information.
            </p>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">9</span>
              Changes to This Policy
            </h2>
            <p className="text-white/70 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <div className="border-t border-white/10"></div>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#862ce0]/30 text-[#b47ef3] text-sm font-bold">10</span>
              Contact Us
            </h2>
            <p className="text-white/70 leading-relaxed mb-6">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-gradient-to-br from-[#862ce0]/20 to-[#c026d3]/10 border border-[#862ce0]/30 rounded-2xl p-6 space-y-3">
              <div className="flex items-center gap-3 text-white/80">
                <i className="ri-mail-line text-[#b47ef3]"></i>
                <a href="mailto:support@4k-dream.com" className="hover:text-[#b47ef3] transition-colors">support@4k-dream.com</a>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <i className="ri-phone-line text-[#b47ef3]"></i>
                <span>+1 (205) 709 2376</span>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <i className="ri-whatsapp-line text-[#b47ef3]"></i>
                <a href="https://wa.me/12057092376" target="_blank" rel="noopener noreferrer" className="hover:text-[#b47ef3] transition-colors">WhatsApp Support</a>
              </div>
            </div>
          </section>

        </div>

        {/* Back link */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors cursor-pointer text-sm">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
          <Link to="/terms" className="inline-flex items-center gap-2 text-[#b47ef3] hover:text-white transition-colors cursor-pointer text-sm">
            View Terms &amp; Conditions
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black/50 border-t border-white/10 py-6 px-4 text-center text-white/40 text-sm">
        <p>4K Dream 2019-2026 © All Rights Reserved &nbsp;·&nbsp;
          <Link to="/privacy" className="hover:text-[#b47ef3] transition-colors">Privacy Policy</Link>
          &nbsp;·&nbsp;
          <Link to="/terms" className="hover:text-[#b47ef3] transition-colors">Terms &amp; Conditions</Link>
        </p>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
